import type { User } from '../../../shared/types';
import { LoggerService } from '../../../shared/services/logger.service';

export interface UserProfileData {
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  fechaNacimiento: string;
  ocupacion: string;
  empresa: string;
  direccion: string;
  // Campos adicionales para personalización
  avatar?: string;
  preferencias?: {
    moneda: string;
    idioma: string;
    tema: 'light' | 'dark';
    notificaciones: boolean;
  };
}

export class UserProfileService {
  private static readonly PROFILE_KEY = 'invirtiendo_user_profile';

  /**
   * Guarda el perfil del usuario en localStorage
   */
  static async saveUserProfile(userId: string, profileData: UserProfileData): Promise<void> {
    try {
      // En producción, esto se guardaría en Firebase
      // Por ahora usamos localStorage con el userId como key
      const key = `${this.PROFILE_KEY}_${userId}`;
      const dataToSave = {
        ...profileData,
        lastUpdated: Date.now(),
        userId
      };
      
      localStorage.setItem(key, JSON.stringify(dataToSave));
      
      LoggerService.info('User profile saved successfully', 'UserProfileService', {
        userId,
        fields: Object.keys(profileData)
      });

      // TODO: Implementar guardado en Firebase cuando esté configurado
      // await this.saveToFirebase(userId, profileData);
      
    } catch (error) {
      LoggerService.error('Error saving user profile', 'UserProfileService', error);
      throw new Error('No se pudo guardar el perfil de usuario');
    }
  }

  /**
   * Recupera el perfil del usuario desde localStorage
   */
  static async getUserProfile(userId: string): Promise<UserProfileData | null> {
    try {
      const key = `${this.PROFILE_KEY}_${userId}`;
      const stored = localStorage.getItem(key);
      
      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored);
      
      // Verificar que los datos pertenezcan al usuario correcto
      if (data.userId !== userId) {
        LoggerService.warn('Profile data userId mismatch', 'UserProfileService', {
          expectedUserId: userId,
          foundUserId: data.userId
        });
        return null;
      }

      // Remover campos internos antes de retornar
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { lastUpdated, userId: _storedUserId, ...profileData } = data;
      
      LoggerService.info('User profile loaded successfully', 'UserProfileService', {
        userId,
        lastUpdated: new Date(lastUpdated).toISOString()
      });

      return profileData as UserProfileData;
      
    } catch (error) {
      LoggerService.error('Error loading user profile', 'UserProfileService', error);
      return null;
    }
  }

  /**
   * Actualiza campos específicos del perfil
   */
  static async updateUserProfile(
    userId: string, 
    updates: Partial<UserProfileData>
  ): Promise<void> {
    try {
      const currentProfile = await this.getUserProfile(userId);
      
      if (!currentProfile) {
        throw new Error('No se encontró el perfil del usuario');
      }

      const updatedProfile = {
        ...currentProfile,
        ...updates
      };

      await this.saveUserProfile(userId, updatedProfile);
      
    } catch (error) {
      LoggerService.error('Error updating user profile', 'UserProfileService', error);
      throw error;
    }
  }

  /**
   * Elimina el perfil del usuario
   */
  static async deleteUserProfile(userId: string): Promise<void> {
    try {
      const key = `${this.PROFILE_KEY}_${userId}`;
      localStorage.removeItem(key);
      
      LoggerService.info('User profile deleted', 'UserProfileService', { userId });
      
    } catch (error) {
      LoggerService.error('Error deleting user profile', 'UserProfileService', error);
      throw error;
    }
  }

  /**
   * Crea un perfil por defecto basado en los datos del usuario autenticado
   */
  static createDefaultProfile(user: User): UserProfileData {
    return {
      nombre: user.displayName?.split(' ')[0] || '',
      apellido: user.displayName?.split(' ').slice(1).join(' ') || '',
      email: user.email || '',
      telefono: '',
      fechaNacimiento: '',
      ocupacion: '',
      empresa: '',
      direccion: '',
      preferencias: {
        moneda: 'ARS',
        idioma: 'es',
        tema: 'light',
        notificaciones: true
      }
    };
  }

  // TODO: Implementar métodos para Firebase cuando esté configurado
  /*
  private static async saveToFirebase(userId: string, profileData: UserProfileData): Promise<void> {
    // Implementar guardado en Firestore
  }

  private static async loadFromFirebase(userId: string): Promise<UserProfileData | null> {
    // Implementar carga desde Firestore
  }
  */
}